import { useEffect, useState } from "react";
import { useTheme } from "../../theme/ThemeContext";
import {
  useGetInventoryPacksQuery,
  useGetCategoriesQuery,
} from "../../store/apiSlice";
import { useStoreInfo } from "../../context/StoreInfoContext";
import { Container } from "../common/Container";
import { AppTypography } from "../common/Typography";
import ProductCard from "../utils/ProductCard";
import { ProductCardSkeleton } from "../utils/SkeletonLoader";
import {
  Box,
  IconButton,
  Pagination,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Search } from "@mui/icons-material";
import noProduct from "../../assets/images/no_product.png";

const Homepage = () => {
  const theme = useTheme();
  const { storeId, loading: storeLoading } = useStoreInfo();

  const { customerId } = useStoreInfo();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(28);
  const [search, setSearch] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [sortBy, setSortBy] = useState("created_date");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [categoryId, setCategoryId] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);

  const isValidSearchValue = (value) => {
    return (
      value &&
      value !== "" &&
      value !== "null" &&
      value !== "undefined" &&
      typeof value === "string" &&
      value.trim() !== ""
    );
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const searchParam = params.get("search");
    const sortByParam = params.get("sortBy");
    const sortOrderParam = params.get("sortOrder");
    const categoryIdParam = params.get("categoryId");

    if (isValidSearchValue(searchParam)) {
      setSearch(searchParam);
      setSearchInput(searchParam);
    }
    if (sortByParam) setSortBy(sortByParam);
    if (sortOrderParam) setSortOrder(sortOrderParam);
    if (categoryIdParam) {
      // Ensure category IDs are treated as strings and filter out empty values
      const categories = categoryIdParam
        .split(",")
        .map((id) => Number(id))
        .filter((id) => id !== "");
      setCategoryId(categoryIdParam);
      setSelectedCategories(categories);
    }
  }, []);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (isValidSearchValue(search)) params.set("search", search);
    if (sortBy) params.set("sortBy", sortBy);
    if (sortOrder) params.set("sortOrder", sortOrder);
    if (categoryId) params.set("categoryId", categoryId);

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState(null, "", newUrl);
  }, [search, sortBy, sortOrder, categoryId]);

  const {
    data: packsData,
    isLoading: packsLoading,
    error: packsError,
    refetch: refetchPacks,
  } = useGetInventoryPacksQuery(
    {
      customerId,
      storeId,
      page: currentPage,
      limit: pageSize,
      ...(isValidSearchValue(search) && { search }),
      sort_by: sortBy,
      sort_order: sortOrder,
      ...(categoryId &&
        categoryId !== "null" &&
        categoryId !== "" && { category_id: categoryId }),
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    data: categoriesData,
    isLoading: categoriesLoading,
    error: categoriesError,
    refetch: refetchCategories,
  } = useGetCategoriesQuery(storeId, {
    refetchOnMountOrArgChange: true,
  });
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [bannerImages, setBannerImages] = useState([]);
  const [isMultipleBanners, setIsMultipleBanners] = useState(false);

  useEffect(() => {
    // Default banner URL
    const defaultBanner =
      "https://developmentprocureasy.s3.ap-south-1.amazonaws.com/swagstore/40/new-banner.png";

    // Get banners from theme
    const banners = theme?.company_banners;
    // Ensure banners is an array and filter out empty/invalid URLs
    const validBanners = Array.isArray(banners)
      ? banners.filter(
          (banner) => banner && banner.trim() !== "" && banner !== " "
        )
      : [];

    // If no banners are configured, use the default banner
    const finalBanners =
      validBanners.length > 0 ? validBanners : [defaultBanner];

    setBannerImages(finalBanners);
    setIsMultipleBanners(finalBanners.length > 1);

    // Reset current banner index if it's out of bounds
    if (currentBannerIndex >= finalBanners.length) {
      setCurrentBannerIndex(0);
    }
    // eslint-disable-next-line
  }, [theme?.company_banners]);

  useEffect(() => {
    if (!isMultipleBanners) return;
    const interval = setInterval(() => {
      setCurrentBannerIndex((prevIndex) =>
        prevIndex === bannerImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);
    return () => clearInterval(interval);
  }, [isMultipleBanners, bannerImages.length]);

  const handlePreviousBanner = () => {
    setCurrentBannerIndex((prevIndex) =>
      prevIndex === 0 ? bannerImages.length - 1 : prevIndex - 1
    );
  };

  const handleNextBanner = () => {
    setCurrentBannerIndex((prevIndex) =>
      prevIndex === bannerImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handleBannerDotClick = (index) => {
    setCurrentBannerIndex(index);
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFilterChange = (newFilters) => {
    // Reset to first page when filters change
    setCurrentPage(1);

    if (newFilters.search !== undefined) {
      const trimmedSearch = newFilters.search?.trim();
      setSearch(isValidSearchValue(trimmedSearch) ? trimmedSearch : null);
    }
    if (newFilters.sortBy !== undefined) setSortBy(newFilters.sortBy);
    if (newFilters.sortOrder !== undefined) setSortOrder(newFilters.sortOrder);
    if (newFilters.categoryId !== undefined) {
      const categoryId = newFilters.categoryId;
      setCategoryId(
        categoryId && categoryId !== "" && categoryId !== "null"
          ? categoryId
          : null
      );
    }
  };

  const handleCategoryChange = (categoryId) => {
    setCurrentPage(1);

    if (categoryId === "all") {
      setSelectedCategories([]);
      setCategoryId(null);
    } else {
      setSelectedCategories((prev) => {
        const newSelected = prev.includes(categoryId)
          ? prev.filter((id) => id !== categoryId)
          : [...prev, categoryId];

        const categoryIdString =
          newSelected.length > 0 ? newSelected.join(",") : null;
        setCategoryId(categoryIdString);

        return newSelected;
      });
    }
  };

  const handleSortChange = (event) => {
    const value = event.target.value;
    if (value === "selling_price_ASC") {
      setSortBy("selling_price");
      setSortOrder("ASC");
    } else if (value === "selling_price_DESC") {
      setSortBy("selling_price");
      setSortOrder("DESC");
    } else if (value === "created_date_DESC") {
      setSortBy("created_date");
      setSortOrder("DESC");
    } else if (value === "created_date_ASC") {
      setSortBy("created_date");
      setSortOrder("ASC");
    }
    setCurrentPage(1);
  };

  useEffect(() => {
    if (!searchInput || searchInput.trim() === "") {
      setSearch(null);
    }
  }, [searchInput]);

  if (packsLoading) {
    return (
      <Container>
        <Box sx={{ py: 4, flex: 1 }}>
          <ProductCardSkeleton />
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {/* Full Screen Banner */}
      <Box
        sx={{
          width: "100vw",
          position: "relative",
          overflow: "hidden",
          backgroundColor: "#f5f5f5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          // On small screens: no aspect ratio, let image determine height
          // On desktop: use aspect ratio for consistent layout
          "@media (min-width: 900px)": {
            aspectRatio: "4/1",
          },
        }}
      >
        {/* Banner Image */}
        <Box
          component="img"
          src={bannerImages[currentBannerIndex]}
          alt={`Company Banner ${currentBannerIndex + 1}`}
          sx={{
            width: "100%",
            height: {
              xs: "auto", // Auto height on mobile to show full image
              md: "100%", // Full height on desktop
            },
            maxHeight: {
              xs: "70vh", // Limit max height on mobile to prevent too tall images
              md: "none",
            },
            objectFit: {
              xs: "contain", // Show full image on small screens
              md: "cover", // Cover on larger screens
            },
            objectPosition: "center",
            transition: "opacity 0.5s ease-in-out",
            display: "block",
          }}
        />
        {/* Navigation Arrows - Only show for multiple banners */}
        {isMultipleBanners && (
          <>
            <IconButton
              onClick={handlePreviousBanner}
              sx={{
                position: "absolute",
                left: 16,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#333",
              }}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              onClick={handleNextBanner}
              sx={{
                position: "absolute",
                right: 16,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#333",
              }}
            >
              <ChevronRight />
            </IconButton>
          </>
        )}
        {/* Dots Indicator - Only show for multiple banners */}
        {isMultipleBanners && (
          <Box
            sx={{
              position: "absolute",
              bottom: 16,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              gap: 1,
            }}
          >
            {bannerImages?.map((_, index) => (
              <Box
                key={index}
                onClick={() => handleBannerDotClick(index)}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor:
                    index === currentBannerIndex
                      ? "rgba(255, 255, 255, 0.9)"
                      : "rgba(255, 255, 255, 0.4)",
                  cursor: "pointer",
                  transition: "background-color 0.3s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Products Section */}
      {packsError && (
        <Container sx={{ padding: "0px", mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src={noProduct}
              alt="No products found"
              sx={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                opacity: 0.8,
              }}
            />
          </Box>
        </Container>
      )}
      {!packsError && (
        <Container
          sx={{ padding: "0px 16px", mb: 3, maxWidth: "90% !important" }}
        >
          <Box id="best-seller-section" mb={3}>
            {/* Header Row with Title, Search, and Sort */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
                alignItems: { xs: "stretch", md: "center" },
                justifyContent: "space-between",
                mb: 4,
              }}
            >
              {/* Page Title - Left Side */}
              <AppTypography
                variant="h4"
                fontWeight={700}
                fontSize="1.5rem"
                style={theme?.typography?.heading}
                sx={{
                  flexShrink: 0,
                }}
              >
                {theme?.homepage_product_heading || "All Products"}
              </AppTypography>

              {/* Search and Sort - Right Side */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  alignItems: { xs: "stretch", md: "center" },
                  flexShrink: 0,
                  width: { xs: "100%", md: "auto" },
                }}
              >
                {/* Search Input with Icon */}
                <Box
                  sx={{
                    position: "relative",
                    maxWidth: { xs: "100%", md: "367px" },
                    width: { xs: "100%", md: "367px" },
                  }}
                >
                  <Search
                    sx={{
                      position: "absolute",
                      left: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#666",
                      fontSize: "18px",
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const trimmedSearch = searchInput?.trim();
                        setSearch(
                          isValidSearchValue(trimmedSearch)
                            ? trimmedSearch
                            : null
                        );
                        setCurrentPage(1); // Reset to first page on search
                      }
                    }}
                    style={{
                      width: "100%",
                      height: "43px",
                      padding: "12px 16px 12px 40px",
                      border: `1px solid #C0C6D4`,
                      borderRadius: "15px",
                      fontSize: "14px",
                      outline: "none",
                      transition: "border-color 0.3s ease",
                      backgroundColor: "#fff",
                      color: theme?.typography?.body?.color || "#333",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor =
                        theme?.app_button_color || "#e74c3c";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = `#C0C6D4`;
                    }}
                  />
                </Box>

                {/* Sort Dropdown */}
                <Box
                  sx={{
                    minWidth: { xs: "100%", md: "180px" },
                    width: { xs: "100%", md: "auto" },
                  }}
                >
                  <FormControl fullWidth size="small">
                    <Select
                      value={`${sortBy}_${sortOrder}`}
                      onChange={handleSortChange}
                      displayEmpty
                      sx={{
                        backgroundColor: "#fff",
                        borderRadius: "15px",
                        height: "43px",
                        fontSize: "13px",
                        fontWeight: 400,
                        color: "#6B7284",
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: `1px solid #C0C6D4`,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          border: `1px solid ${
                            theme?.app_button_color || "#e74c3c"
                          }`,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: `1px solid ${
                            theme?.app_button_color || "#e74c3c"
                          }`,
                        },
                      }}
                    >
                      <MenuItem
                        value="selling_price_DESC"
                        sx={{ fontSize: "14px", fontWeight: 400 }}
                      >
                        Price: High to Low
                      </MenuItem>
                      <MenuItem
                        value="selling_price_ASC"
                        sx={{ fontSize: "14px", fontWeight: 400 }}
                      >
                        Price: Low to High
                      </MenuItem>
                      <MenuItem
                        value="created_date_DESC"
                        sx={{ fontSize: "14px", fontWeight: 400 }}
                      >
                        Date: Latest
                      </MenuItem>
                      <MenuItem
                        value="created_date_ASC"
                        sx={{ fontSize: "14px", fontWeight: 400 }}
                      >
                        Date: Oldest
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Categories Filter Bar */}
          {categoriesData?.result &&
            Array.isArray(categoriesData.result) &&
            categoriesData.result.length > 0 && (
              <Box
                sx={{
                  backgroundColor: "white",
                  borderRadius: "20px",
                  padding: "20px",
                  boxShadow: "0px 4px 6px rgba(205, 205, 205, 0.4)",
                  mb: 7,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    overflowX: "auto",
                    "&::-webkit-scrollbar": {
                      height: "4px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#f1f1f1",
                      borderRadius: "2px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#c1c1c1",
                      borderRadius: "2px",
                    },
                  }}
                >
                  <AppTypography
                    variant="body1"
                    style={theme?.typography?.body}
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      color: "#6B7284",
                      flexShrink: 0,
                    }}
                  >
                    Categories:
                  </AppTypography>

                  {/* All Categories Button */}
                  <Box
                    onClick={() => handleCategoryChange("all")}
                    sx={{
                      backgroundColor:
                        selectedCategories.length === 0
                          ? theme?.app_button_color || "#667eea"
                          : "#F2F2F2",
                      color: selectedCategories.length === 0 ? "white" : "#666",
                      padding: "8px 16px",
                      borderRadius: "20px",
                      cursor: "pointer",
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      transition: "all 0.2s ease",
                      flexShrink: 0,
                      "&:hover": {
                        backgroundColor:
                          selectedCategories.length === 0
                            ? theme?.app_button_color || "#5a6fd8"
                            : "#e8e8e8",
                      },
                    }}
                  >
                    All
                  </Box>

                  {/* Individual Category Buttons */}
                  {categoriesData?.result?.map((category) => (
                    <Box
                      key={category.id}
                      onClick={() => handleCategoryChange(category.id)}
                      sx={{
                        backgroundColor: selectedCategories.includes(
                          category.id
                        )
                          ? theme?.app_button_color || "#667eea"
                          : "#F2F2F2",
                        color: selectedCategories.includes(category.id)
                          ? "white"
                          : "#6B7284",
                        padding: "8px 16px",
                        borderRadius: "20px",
                        cursor: "pointer",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        transition: "all 0.2s ease",
                        flexShrink: 0,
                        "&:hover": {
                          backgroundColor: selectedCategories.includes(
                            category.id
                          )
                            ? theme?.app_button_color || "#5a6fd8"
                            : "#e8e8e8",
                        },
                      }}
                    >
                      {category.name}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

          {/* Products Grid */}
          <Box
            sx={{
              width: "100%",
              mb: 3,
            }}
          >
            {packsData?.result?.list?.length > 0 ? (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, 1fr)",
                      md: "repeat(3, 1fr)",
                      lg: "repeat(4, 1fr)",
                      xl: "repeat(4, 1fr)",
                    },
                    gap: { xs: 2, md: 2, lg: 4 },
                    "@media (min-width:1700px)": {
                      gridTemplateColumns: "repeat(5, 1fr)",
                    },
                  }}
                >
                  {packsData?.result?.list?.map((pack, index) => {
                    // Create a unique key using multiple identifiers and index
                    const firstProduct = pack.products && pack.products[0];
                    const baseKey =
                      pack.id || firstProduct?.product_id || pack._id;
                    const uniqueKey = `${baseKey}-${index}`;
                    return <ProductCard key={uniqueKey} pack={pack} />;
                  })}
                </Box>

                {/* Pagination */}
                {packsData?.result?.total_pages > 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mt: 4,
                      mb: 2,
                    }}
                  >
                    <Pagination
                      count={packsData.result.total_pages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      size="large"
                      showFirstButton
                      showLastButton
                      sx={{
                        "& .MuiPaginationItem-root": {
                          color: theme?.typography?.body?.color || "#333",
                          "&.Mui-selected": {
                            backgroundColor:
                              theme?.app_button_color || "#e74c3c",
                            color: theme?.app_button_text_color || "#fff",
                            "&:hover": {
                              backgroundColor:
                                theme?.hover_button_color || "#e74c3c",
                            },
                          },
                          "&:hover": {
                            backgroundColor: "rgba(231, 76, 60, 0.1)",
                          },
                        },
                      }}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={noProduct}
                  alt="No products found"
                  sx={{
                    width: "80%",
                    height: "auto",
                    objectFit: "contain",
                    opacity: 0.8,
                    mb: 3,
                  }}
                />
              </Box>
            )}
          </Box>
        </Container>
      )}
    </Box>
  );
};

export default Homepage;
